import React, { useState, useEffect, useRef } from "react";
import { Outlet, useParams, useLocation } from "react-router-dom";
import { useSpring, animated } from "react-spring";
import PageLayout from "../Dashboard/MainContent/pages/PageLayout";
import Navbar, { NavBrand, NavItem, NavItemButton } from "../../layouts/Navbar";
import { useRedux } from "../../Hooks/useRedux";
import { Icon } from "@iconify/react";
import Logo from "../../layouts/Logo";
import Button from "../../layouts/Button";

import { useNavigate } from "react-router-dom";

// actions
import { getProfile } from "../../redux/actions/authAction";
import { showHomeSidebar } from "../../redux/actions/helperAction";

import DotLoader from "../../layouts/DotLoader";
import Footer from "../Footer";
import clsx from "clsx";
import consoleHelper from "../../utils/consoleHelper";

const Pages = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  //   consoleHelper("location", location);

  const {
    data: {
      homeSidebar,
      user: { user },
    },
    dispatch,
  } = useRedux(["homeSidebar", "user"]);

  consoleHelper("homeSidebar", homeSidebar);

  const [isGettingUser, setIsGettingUser] = useState(false);

  useEffect(() => {
    setIsGettingUser(() => true);
    dispatch(
      getProfile({
        err: (m) => {
          setIsGettingUser(() => false);
        },
        done: (m) => {
          setIsGettingUser(() => false);
        },
      })
    );
  }, []);

  const navItems = [
    { title: "Home", path: "/" },
    { title: "Pricing", path: "/pricing" },
    { title: "Features", path: "/features" },
    { title: "APIs", path: "/apis" },
    { title: "How Does It Work", path: "/how-does-it-work", mr: "1.5rem" },
  ];

  const clickAwayRef = useRef(null);
  useEffect(() => {
    function clickAway(e) {
      if (clickAwayRef?.current) {
        if (!clickAwayRef?.current?.contains(e.target)) {
          homeSidebar && dispatch(showHomeSidebar(false));
        }
      }
    }

    document.addEventListener("click", clickAway);

    return () => {
      document.removeEventListener("click", clickAway);
    };
  }, [homeSidebar]);

  const sidebarOpenAnimation = useSpring({
    from: { left: "0px" },
    to: { left: homeSidebar ? "0px" : "-300px" },
    config: { duration: 90 },
  });

  return (
    <div className={clsx("flex flex-col justify-center min-h-screen")}>
      {/* sidebar */}
      <div
        className={clsx(
          "md:hidden",
          homeSidebar
            ? "bg-backdropBackground fixed inset-0 z-[20] xs:block"
            : " transition-all bg-white absolute top-0 -left-[300px]"
        )}
      >
        <animated.div
          className={clsx(
            "w-[260px] min-h-screen absolute bg-white overflow-hidden md:hidden "
          )}
          ref={clickAwayRef}
          style={sidebarOpenAnimation}
        >
          <NavBrand normalResponsiveBehavior={false} className="mt-2 pl-6">
            <Logo className="w-[2.5rem]" />
          </NavBrand>

          <div className="flex flex-col items-center mt-20 gap-8">
            {navItems.map((item, index) => {
              return (
                <ANavItem item={item} key={item.path} pathname={pathname} />
              );
            })}
          </div>

          <div className="mt-10 text-center">
            {isGettingUser ? (
              <div>
                <DotLoader />
              </div>
            ) : !user || user?.err || !localStorage.getItem("dat") ? (
              <Button
                variant=""
                className="w-[175px]"
                onClick={() => navigate("/auth/login")}
              >
                Login
              </Button>
            ) : (
              <Button
                variant=""
                className="py-1.5"
                onClick={() => navigate("/b/dashboard")}
              >
                Go To Dashboard
              </Button>
            )}
          </div>
        </animated.div>
      </div>
      {/* Navbar */}
      <Navbar
        navBrand={
          <NavBrand normalResponsiveBehavior={false} className="pl-0">
            <Logo className="w-[2.5rem]" />
          </NavBrand>
        }
        childClassName="max-w-[1280px] mx-auto px-4 1300px:px-0"
      >
        <div className="xs:hidden md:flex">
          {navItems.map((item, index) => {
            return <ANavItem item={item} key={item.path} pathname={pathname} />;
          })}
        </div>

        <div className="">
          {isGettingUser ? (
            <DotLoader />
          ) : !user || user?.err || !localStorage.getItem("dat") ? (
            <Button
              variant="secondary"
              className="py-1.5"
              onClick={() => navigate("/auth/login")}
            >
              Login
            </Button>
          ) : (
            <>
              <Button
                variant=""
                className="xs:hidden 400px:block py-1.5"
                onClick={() => navigate("/b/dashboard")}
              >
                Go To Dashboard
              </Button>
              <Button
                variant=""
                className="xs:block 400px:hidden py-1.5"
                onClick={() => navigate("/b/dashboard")}
              >
                Dashboard
              </Button>
              {/* <div>Go To Dashboard</div> */}
            </>
          )}
        </div>
        <NavItem className="text-primary-200 md:hidden">
          <Icon
            className="cursor-pointer"
            icon="eva:menu-outline"
            height={26}
            width={26}
            onClick={() => {
              dispatch(showHomeSidebar(!homeSidebar));
            }}
          />
        </NavItem>
      </Navbar>

      {/* Content */}
      <Outlet />

      <div className="flex-1" />

      {/* Footer */}
      <div className="mt-5 max-w-[1280px] w-full mx-auto">
        <Footer />
      </div>
    </div>
  );
};

export default Pages;

const ANavItem = ({ item, pathname }) => {
  const navigate = useNavigate();

  const isActiveNav = pathname === item.path;

  const navItemRef = useRef(null);

  const activeNavItemWidth =
    navItemRef && navItemRef.current && navItemRef.current.clientWidth;

  //   consoleHelper("activeNavItemWidth", activeNavItemWidth);

  const activeNavStyle = useSpring({
    from: { transform: "scaleX(0)", height: "0px", transformOrigin: "center" },
    to: {
      height: isActiveNav ? "2px" : "0px",
      transform: isActiveNav ? "scaleX(1)" : "scaleX(0)",
      transformOrigin: "center",
    },
    config: { duration: 150 },
  });

  return (
    <div
      className={clsx(
        "mx-3 font-semibold text-primary-100 font-lg tracking-wider cursor-pointer relative",
        ""
      )}
      style={{ marginRight: item.mr }}
      onClick={() => navigate(item.path)}
    >
      <div ref={isActiveNav ? navItemRef : null}>{item.title}</div>

      {isActiveNav && (
        <animated.span
          className={clsx("bg-primary-100 absolute w-5 -bottom-1", "")}
          // style={{ transform: "scale(0.5)" }}
          style={{ ...activeNavStyle, width: activeNavItemWidth }}
        ></animated.span>
      )}
    </div>
  );
};
